import { useState, useEffect, useRef } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import {
	Card,
} from '@/components/ui/card'
import {
	Lightbulb,
	Filter,
	RotateCcw,
	AlertTriangle,
	Lock,
	Loader2,
} from 'lucide-react'
import { FeatureRequestGrid } from '@/components/feature-requests/FeatureRequestGrid'
import { FeatureRequestDetailView } from '@/components/feature-requests/FeatureRequestDetailView'
import { useProgramContext } from '@/context/ProgramContext'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { motion } from 'framer-motion'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { FeatureRequest } from '@/lib/services/feedback'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationPrevious,
	PaginationNext,
	PaginationLink,
	PaginationEllipsis,
} from '@/components/ui/pagination'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from '@/hooks/useTranslation'

// Define the expected API response structure
interface FeatureApiResponse {
	data: FeatureRequest[]
	totalCount: number
	viewableLimit: number
	hiddenCount: number
	isPro: boolean
	hasMore: boolean
	currentPage: number
	itemsPerPage: number
	totalPages: number
}

const getStatusOptions = (t: (key: string) => string) => [
	{ label: t('featureRequests.statusNew'), value: 'New' },
	{ label: t('featureRequests.statusUnderConsideration'), value: 'Under Consideration' },
	{ label: t('featureRequests.statusPlanned'), value: 'Planned' },
	{ label: t('featureRequests.statusInProgress'), value: 'In Progress' },
	{ label: t('featureRequests.statusLaunched'), value: 'Launched' },
	{ label: t('featureRequests.statusDeclined'), value: 'Declined' },
]

const getSortOptions = (t: (key: string) => string) => [
	{ label: t('featureRequests.sortUpvotesHighToLow'), value: 'upvotes_desc' },
	{ label: t('featureRequests.sortUpvotesLowToHigh'), value: 'upvotes_asc' },
	{ label: t('featureRequests.sortNewestFirst'), value: 'created_desc' },
	{ label: t('featureRequests.sortOldestFirst'), value: 'created_asc' },
	{ label: t('featureRequests.sortRecentlyUpdated'), value: 'updated_desc' },
	{ label: t('featureRequests.sortLeastRecentlyUpdated'), value: 'updated_asc' },
	{ label: t('featureRequests.sortTitleAZ'), value: 'title_asc' },
	{ label: t('featureRequests.sortTitleZA'), value: 'title_desc' },
]

export default function FeatureRequests() {
	const { t } = useTranslation()
	const {
		currentProgram,
		programId,
		loading: programLoading,
	} = useProgramContext()
	const { session, loading: authLoading } = useAuth()
	const statusOptions = getStatusOptions(t)
	const sortOptions = getSortOptions(t)
	const [search, setSearch] = useState('')
	const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
		'New',
		'Under Consideration',
		'Planned',
		'In Progress',
	])
	const [sort, setSort] = useState('upvotes_desc')
	const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)
	const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
		null
	)
	const [activeTab, setActiveTab] = useState('all')
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()

	const [currentPage, setCurrentPage] = useState(
		parseInt(searchParams.get('page') || '1', 10)
	)
	const itemsPerPage = 10
	const justClosedModalRef = useRef(false)

	useEffect(() => {
		const featureIdFromUrl = searchParams.get('featureId')
		if (featureIdFromUrl) {
			if (justClosedModalRef.current) {
				justClosedModalRef.current = false
				return
			}
			if (featureIdFromUrl !== selectedFeatureId) {
				setSelectedFeatureId(featureIdFromUrl)
			}
		} else {
			justClosedModalRef.current = false
		}
	}, [searchParams, selectedFeatureId])

	const {
		data: apiResponse,
		isLoading: queryIsLoading,
		isFetching: queryIsFetching,
		error: queryError,
		refetch,
	} = useQuery<FeatureApiResponse, Error>({
		queryKey: [
			'features',
			programId,
			{
				search,
				status: selectedStatuses,
				sort,
				dateRange,
				activeTab,
				currentPage,
				itemsPerPage,
			},
		],
		queryFn: async () => {
			if (!programId || !session) {
				return {
					data: [],
					totalCount: 0,
					viewableLimit: 0,
					hiddenCount: 0,
					isPro: false,
					hasMore: false,
					currentPage: 1,
					itemsPerPage,
					totalPages: 0,
				}
			}

			const params: Record<string, string> = {
				programId,
				limit: String(itemsPerPage),
				page: String(currentPage),
			}
			if (search) params.search = search

			if (activeTab !== 'all') {
				params.tab = activeTab
			} else if (selectedStatuses.length > 0) {
				params.statuses = selectedStatuses.join(',')
			}

			if (sort) params.sort = sort
			if (dateRange?.from) params.fromDate = dateRange.from.toISOString()
			if (dateRange?.to)
				params.toDate = new Date(
					dateRange.to.setHours(23, 59, 59, 999)
				).toISOString()

			const url = `/api/features/protected?${new URLSearchParams(
				params
			).toString()}`

			return apiClient<FeatureApiResponse>(url)
		},
		enabled: !!programId && !!session && !authLoading,
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	})

	const featuresToDisplay = apiResponse?.data ?? []

	const isLoading = (queryIsLoading || authLoading) && !apiResponse
	const isUpdating = queryIsFetching && !!apiResponse

	// Track previous filter values to detect actual changes (not just page changes)
	const prevFiltersRef = useRef({
		search,
		selectedStatuses: selectedStatuses.join(','),
		sort,
		dateRange: dateRange
			? `${dateRange.from?.toISOString()}-${dateRange.to?.toISOString()}`
			: '',
		activeTab,
		programId,
	})

	useEffect(() => {
		const prevFilters = prevFiltersRef.current
		const currentFilters = {
			search,
			selectedStatuses: selectedStatuses.join(','),
			sort,
			dateRange: dateRange
				? `${dateRange.from?.toISOString()}-${dateRange.to?.toISOString()}`
				: '',
			activeTab,
			programId,
		}

		// Only reset page if actual filters changed (not just page number)
		const filtersChanged =
			prevFilters.search !== currentFilters.search ||
			prevFilters.selectedStatuses !== currentFilters.selectedStatuses ||
			prevFilters.sort !== currentFilters.sort ||
			prevFilters.dateRange !== currentFilters.dateRange ||
			prevFilters.activeTab !== currentFilters.activeTab ||
			prevFilters.programId !== currentFilters.programId

		if (filtersChanged && currentPage !== 1) {
			setCurrentPage(1)
			setSearchParams(
				(prev) => {
					prev.set('page', '1')
					return prev
				},
				{ replace: true }
			)
		}

		// Update ref for next comparison
		prevFiltersRef.current = currentFilters
	}, [
		search,
		selectedStatuses,
		sort,
		dateRange,
		activeTab,
		programId,
		currentPage,
		setSearchParams,
	])

	useEffect(() => {
		const pageFromUrl = parseInt(searchParams.get('page') || '1', 10)
		if (pageFromUrl !== currentPage && pageFromUrl > 0) {
			setCurrentPage(pageFromUrl)
		}
	}, [searchParams])

	const handlePageChange = (newPage: number) => {
		if (newPage === currentPage) return
		setCurrentPage(newPage)
		setSearchParams(
			(prev) => {
				prev.set('page', String(newPage))
				return prev
			},
			{ replace: true }
		)
	}

	const handleReset = () => {
		setSearch('')
		setSelectedStatuses([
			'New',
			'Under Consideration',
			'Planned',
			'In Progress',
			'Launched',
			'Declined',
		])
		setSort('upvotes_desc')
		setDateRange(undefined)
		setActiveTab('all')
		if (currentPage !== 1) handlePageChange(1)
		else setSearchParams({}, { replace: true })
	}

	const toggleStatus = (status: string) => {
		setSelectedStatuses((prev) =>
			prev.includes(status)
				? prev.filter((s) => s !== status)
				: [...prev, status]
		)
	}

	const handleTabChange = (value: string) => {
		setActiveTab(value)
		setSelectedStatuses([
			'New',
			'Under Consideration',
			'Planned',
			'In Progress',
			'Launched',
			'Declined',
		])
		if (currentPage !== 1) handlePageChange(1)
	}

	if (isLoading) {
		return (
			<div className="container py-6 space-y-6">
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3 }}>
					<div className="flex items-center gap-3">
						<Skeleton className="h-10 w-10 rounded-lg" />
						<div>
							<Skeleton className="h-6 w-48" />
							<Skeleton className="h-4 w-64 mt-1" />
						</div>
					</div>
				</motion.div>
				<Card className="p-4 border-border/50 shadow-sm">
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
						{[...Array(5)].map((_, i) => (
							<Skeleton key={i} className="h-10 w-full" />
						))}
					</div>
				</Card>
				<Skeleton className="h-10 w-full rounded-md" />
				<div className="space-y-4">
					{[...Array(itemsPerPage)].map((_, i) => (
						<Card key={i} className="flex flex-row h-[140px]">
							<div className="flex-1 p-6">
								<Skeleton className="h-6 w-3/4 mb-2" />
								<Skeleton className="h-4 w-full mb-1" />
								<Skeleton className="h-4 w-2/3 mb-3" />
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Skeleton className="h-5 w-24" />
										<Skeleton className="h-4 w-16" />
									</div>
									<Skeleton className="h-4 w-16" />
								</div>
							</div>
							<div className="min-w-[120px] flex flex-col items-center justify-between border-l border-border/50 p-4">
								<div className="self-end">
									<Skeleton className="h-8 w-8" />
								</div>
								<div className="text-center">
									<Skeleton className="h-16 w-16 rounded-lg bg-primary/10" />
								</div>
							</div>
						</Card>
					))}
				</div>
			</div>
		)
	}
	if (queryError) {
		return (
			<div className="container py-12 flex flex-col items-center justify-center">
				<AlertTriangle className="h-12 w-12 text-destructive mb-4" />
				<h2 className="text-xl font-semibold text-destructive mb-2">
					Error Loading Features
				</h2>
				<p className="text-muted-foreground mb-4">
					We couldn't fetch the feature requests. Please try again later.
				</p>
				<pre className="text-xs bg-muted p-2 rounded-md text-destructive-foreground max-w-md overflow-auto">
					{queryError.message}
				</pre>
				<Button onClick={() => refetch()} className="mt-4">
					Retry
				</Button>
			</div>
		)
	}
	if (!programId && !authLoading && !programLoading) {
		return (
			<div className="container py-12 flex flex-col items-center justify-center">
				<Lightbulb className="h-12 w-12 text-muted-foreground mb-4" />
				<h2 className="text-xl font-semibold text-foreground mb-2">
					No Program Selected
				</h2>
				<p className="text-muted-foreground">
					Please select a program to view its feature requests.
				</p>
			</div>
		)
	}

	const totalPages = apiResponse?.totalPages || 1
	const totalItems = apiResponse?.totalCount ?? 0
	const viewableLimitNum = apiResponse?.viewableLimit ?? 0
	const hiddenCountNum = apiResponse?.hiddenCount ?? 0

	return (
		<div className="container py-6 space-y-6 ">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Lightbulb className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('featureRequests.featureRequests')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('featureRequests.browseAndManage', { program: currentProgram?.name || t('featureRequests.yourProgram') })}
						</p>
					</div>
				</div>
			</motion.div>

			{/* Filters */}
			<Card className="p-4 border-border/50 shadow-sm">
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
					<Input
						placeholder={t('featureRequests.searchFeatures')}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="xl:col-span-1 text-sm"
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className="w-full justify-between text-sm"
								disabled={activeTab !== 'all'}>
								{t('featureRequests.statusFilter')} (
								{selectedStatuses.length === statusOptions.length
									? t('featureRequests.all')
									: selectedStatuses.length}
								)<span className="sr-only">{t('featureRequests.toggleStatusMenu')}</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-56 p-2">
							<div className="flex items-center space-x-2 p-1 border-b">
								<Button
									variant="ghost"
									size="sm"
									className="w-full text-left justify-start h-6 font-medium"
									onClick={() =>
										setSelectedStatuses(statusOptions.map((opt) => opt.value))
									}>
									{t('featureRequests.selectAll')}
								</Button>
							</div>
							<div className="flex items-center space-x-2 p-1 border-b">
								<Button
									variant="ghost"
									size="sm"
									className="w-full text-left justify-start h-6 font-medium"
									onClick={() => setSelectedStatuses([])}>
									{t('featureRequests.deselectAll')}
								</Button>
							</div>
							{statusOptions.map((option) => (
								<div
									key={option.value}
									className="flex items-center space-x-2 p-2">
									<Checkbox
										id={`status-${option.value}`}
										checked={selectedStatuses.includes(option.value)}
										onCheckedChange={() => toggleStatus(option.value)}
									/>
									<label
										htmlFor={`status-${option.value}`}
										className="text-sm font-medium cursor-pointer flex-1">
										{option.label}
									</label>
								</div>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
					<Select value={sort} onValueChange={setSort}>
						<SelectTrigger className="text-sm">
							<SelectValue placeholder={t('featureRequests.sortBy')} />
						</SelectTrigger>
						<SelectContent>
							{sortOptions.map((o) => (
								<SelectItem key={o.value} value={o.value} className="text-sm">
									{o.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="md:col-span-2 lg:col-span-1 xl:col-span-1">
						<DatePickerWithRange
							date={dateRange}
							setDate={(newRange) => setDateRange(newRange)}
						/>
					</div>
					<div className="flex justify-end items-end md:col-start-2 lg:col-start-3 xl:col-start-5">
						<Button variant="outline" onClick={handleReset} size="sm">
							<RotateCcw className="mr-2 h-4 w-4" /> {t('featureRequests.resetFilters')}
						</Button>
					</div>
				</div>
			</Card>

			{/* Hidden Count Banner */}
			{apiResponse && !apiResponse.isPro && hiddenCountNum > 0 && (
				<Alert
					variant="default"
					className="mb-6 bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700">
					<Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
					<AlertTitle className="font-semibold text-amber-700 dark:text-amber-300">
						{t('featureRequests.unlockMoreInsights')}
					</AlertTitle>
					<AlertDescription className="text-amber-600 dark:text-amber-400">
						{t('featureRequests.collectedFeatureRequests', { 
							count: totalItems,
							limit: viewableLimitNum,
							hidden: hiddenCountNum
						})}
						<Button
							variant="link"
							className="p-0 h-auto text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1 font-medium"
							onClick={() => navigate('/settings?tab=billing')}>
							{t('featureRequests.upgradeToProToSeeAll')}
						</Button>
					</AlertDescription>
				</Alert>
			)}

			{/* Tabs and Grid */}
			<Tabs
				value={activeTab}
				onValueChange={handleTabChange}
				className="w-full">
				{/* <div className="border-b border-border/50">
					<TabsList className="inline-grid w-full grid-flow-col auto-cols-max md:w-auto gap-1 p-0 bg-transparent">
						<TabsTrigger
							value="all"
							className="px-3 py-1.5 text-sm data-[state=active]:bg-muted data-[state=active]:shadow-none rounded-t-md data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary">
							{t('featureRequests.all')} ({totalItems})
						</TabsTrigger>
						{statusOptions.filter((opt) => opt.value !== 'all').map(
							(option) => (
								<TabsTrigger
									key={option.value}
									value={option.value}
									className="px-3 py-1.5 text-sm whitespace-nowrap data-[state=active]:bg-muted data-[state=active]:shadow-none rounded-t-md data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary">
									{option.label}
								</TabsTrigger>
							)
						)}
					</TabsList>
				</div> */}
				<div className="mt-4 relative">
					{isUpdating && (
						<div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
							<Loader2 className="h-6 w-6 animate-spin text-primary" />
						</div>
					)}
					<FeatureRequestGrid
						features={featuresToDisplay}
						isLoading={isLoading}
						isEmpty={
							!isLoading &&
							!isUpdating &&
							featuresToDisplay.length === 0 &&
							!(
								!apiResponse?.isPro &&
								hiddenCountNum > 0 &&
								featuresToDisplay.length < itemsPerPage
							)
						}
						totalCount={totalItems}
						onFeatureSelect={(id) => {
							setSelectedFeatureId(id)
							if (id)
								setSearchParams(
									(prev) => {
										prev.set('featureId', id)
										return prev
									},
									{ replace: true }
								)
							else
								setSearchParams(
									(prev) => {
										prev.delete('featureId')
										return prev
									},
									{ replace: true }
								)
						}}
						isPro={apiResponse?.isPro ?? true}
						hiddenCount={hiddenCountNum}
						itemsPerPage={itemsPerPage}
						viewableLimit={viewableLimitNum}
					/>
				</div>
			</Tabs>

			{apiResponse && totalPages > 1 && (
				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								onClick={() =>
									currentPage > 1 &&
									!isUpdating &&
									handlePageChange(currentPage - 1)
								}
								aria-disabled={currentPage === 1 || isUpdating}
								className={cn(
									(currentPage === 1 || isUpdating) &&
										'pointer-events-none opacity-50'
								)}
								href="#"
							/>
						</PaginationItem>

						{[...Array(totalPages)].map((_, i) => {
							const pageNum = i + 1
							if (
								pageNum === 1 ||
								pageNum === totalPages ||
								(pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
							) {
								return (
									<PaginationItem key={pageNum}>
										<PaginationLink
											onClick={() => !isUpdating && handlePageChange(pageNum)}
											isActive={currentPage === pageNum}
											aria-disabled={isUpdating}
											className={cn(
												isUpdating &&
													currentPage !== pageNum &&
													'pointer-events-none opacity-60'
											)}
											href="#">
											{pageNum}
										</PaginationLink>
									</PaginationItem>
								)
							}
							if (
								(pageNum === currentPage - 2 && currentPage > 3) ||
								(pageNum === currentPage + 2 && currentPage < totalPages - 2)
							) {
								return (
									<PaginationItem key={pageNum}>
										<PaginationEllipsis />
									</PaginationItem>
								)
							}
							return null
						})}

						<PaginationItem>
							<PaginationNext
								onClick={() =>
									currentPage < totalPages &&
									!isUpdating &&
									handlePageChange(currentPage + 1)
								}
								aria-disabled={currentPage === totalPages || isUpdating}
								className={cn(
									(currentPage === totalPages || isUpdating) &&
										'pointer-events-none opacity-50'
								)}
								href="#"
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			)}

			{selectedFeatureId && (
				<FeatureRequestDetailView
					featureId={selectedFeatureId}
					onClose={() => {
						justClosedModalRef.current = true
						setSelectedFeatureId(null)
						setSearchParams(
							(prev) => {
								prev.delete('featureId')
								return prev
							},
							{ replace: true }
						)
					}}
					onUpdate={() => {
						refetch()
					}}
				/>
			)}
		</div>
	)
}
